import { Link, Navigate, useParams } from 'react-router-dom';
import './BlogPage.css';

const CHROME_EXTENSION_URL = 'https://chromewebstore.google.com/detail/bdglndigkfjkelmkiiabpdjnkhokifbe?utm_source=item-share-cb';
const DEFAULT_CTA_PRIMARY = { label: 'Start using GoodGPT', href: '/' };
const DEFAULT_CTA_SECONDARY = { label: 'Explore the blog index', href: '/blog' };

function postPath(slug) {
  return `/blog/${slug}`;
}

export function faqJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

const BLOG_POSTS_DATA = [
  {
    slug: 'persistent-memory',
    badge: 'Core Feature',
    title: 'Persistent Memory in AI: Why GoodGPT Remembers What Matters',
    metaTitle: 'Persistent Memory in AI: Why GoodGPT Remembers What Matters',
    description: 'Learn how persistent memory helps GoodGPT remember goals, preferences, and context so every session gets more useful over time.',
    excerpt: 'Persistent memory turns an AI assistant from a one-off chat tool into a system that can carry your goals, preferences, and project context forward.',
    keywords: 'persistent memory AI, memory-based assistant, contextual AI, GoodGPT memory, personalized AI assistant',
    stats: [
      { value: 'Context-aware', label: 'Carries goals and preferences across sessions' },
      { value: 'Less repetition', label: 'Reduces the need to re-explain background' },
      { value: 'Personalized', label: 'Makes future replies more relevant' },
      { value: 'Reusable', label: 'Works across chat, research, and planning' },
    ],
    featured: {
      title: 'What persistent memory changes',
      body: 'An AI assistant with memory does not just answer the current prompt. It can connect today’s request to yesterday’s goal, the tone you prefer, and the work you have already done.',
      items: [
        { title: 'Recall', text: 'Remember the same project, task, or relationship across multiple sessions.' },
        { title: 'Continuity', text: 'Keep work moving without making you start over each time you return.' },
        { title: 'Personalization', text: 'Adapt replies to the preferences you have already expressed.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Why it matters',
        title: 'Persistent memory makes AI useful after the first conversation.',
        body: 'Without memory, every prompt is isolated. That is fine for quick answers, but not for real work. GoodGPT uses memory to preserve the context that matters, so the assistant can stay aligned with your goals over time.',
        points: [
          'It remembers recurring goals, preferences, and project notes.',
          'It keeps useful context close so you do not need to restate it.',
          'It improves the quality of follow-up conversations and decisions.',
        ],
      },
      {
        eyebrow: 'Productivity impact',
        title: 'You spend less time reintroducing yourself.',
        body: 'Persistent memory is especially helpful when you revisit the same topic every day or every week. Instead of rebuilding the setup from scratch, GoodGPT can move straight into the next useful step.',
        points: [
          'Faster project handoffs between sessions.',
          'Less copy-paste repetition across tasks.',
          'More time spent on the actual work, not the setup.',
        ],
      },
      {
        eyebrow: 'Best use cases',
        title: 'It is strongest for ongoing work.',
        body: 'Memory matters most when the assistant is part of a long-running process: research, communication, planning, learning, and anything that benefits from a consistent thread.',
        points: [
          'Research projects that evolve over days or weeks.',
          'Communication where tone and history matter.',
          'Personal planning where goals change gradually.',
        ],
      },
      {
        eyebrow: 'How GoodGPT uses it',
        title: 'GoodGPT keeps the thread without making it visible noise.',
        body: 'The goal is not to show every memory item. The goal is to use relevant context when it helps the answer. That keeps the experience lightweight while still making the assistant feel informed.',
        points: [
          'Relevant context shows up when it improves the answer.',
          'The assistant can stay consistent with your prior choices.',
          'You keep control over the memory experience in the app.',
        ],
      },
    ],
    faq: [
      { q: 'Does persistent memory store everything I say?', a: 'No. The point is to preserve useful context, not to turn every sentence into a permanent record. GoodGPT is designed to use relevant memories when they help the conversation.' },
      { q: 'Why is memory important for SEO content?', a: 'People search for problems, not just products. Persistent memory helps GoodGPT stand out as an assistant that solves ongoing workflows instead of one-off prompts.' },
      { q: 'Who benefits most from memory-based AI?', a: 'People who return to the same projects, recurring workflows, or long-term goals usually benefit the most, because continuity matters more than a single answer.' },
    ],
    relatedSlugs: ['smart-chat', 'literature-discovery'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read the smart chat guide', href: '/blog/smart-chat' },
  },
  {
    slug: 'literature-discovery',
    badge: 'Research Feature',
    title: 'AI Literature Discovery: Research Faster Without Losing the Plot',
    metaTitle: 'AI Literature Discovery: Research Faster Without Losing the Plot',
    description: 'GoodGPT literature discovery helps you search, scan, and summarize sources faster so you can turn reading into usable insight.',
    excerpt: 'When research turns into tab overload, literature discovery helps you find, compare, and summarize information without losing the thread.',
    keywords: 'AI literature discovery, research assistant, source summarization, reading workflow, GoodGPT research',
    stats: [
      { value: 'Source-aware', label: 'Helps surface relevant reading faster' },
      { value: 'Summaries', label: 'Turns long material into usable notes' },
      { value: 'Contextual', label: 'Keeps the topic tied to your goals' },
      { value: 'Efficient', label: 'Reduces tab switching and note chaos' },
    ],
    featured: {
      title: 'Why literature discovery matters',
      body: 'Research is rarely a single search. It is a chain of questions, sources, summaries, and follow-ups. GoodGPT makes that chain easier to manage by keeping the reading process organized.',
      items: [
        { title: 'Find', text: 'Move from a broad topic to the most relevant reading faster.' },
        { title: 'Summarize', text: 'Capture the key idea before the details get buried.' },
        { title: 'Synthesize', text: 'Keep related insights together so you can write or decide later.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Research workflow',
        title: 'Search, read, summarize, and move on with clarity.',
        body: 'A good literature workflow should reduce friction. GoodGPT helps you move from a topic to the material you need, then from that material to notes you can use in the next step.',
        points: [
          'Use it to narrow broad research questions quickly.',
          'Turn source material into readable summaries.',
          'Return to the same topic with the same context intact.',
        ],
      },
      {
        eyebrow: 'Who it helps',
        title: 'Students, founders, marketers, and analysts all benefit.',
        body: 'Anyone who reads a lot for work can use literature discovery to save time. The common pattern is simple: you need answers, but you also need the context behind them.',
        points: [
          'Students can study faster and write with more confidence.',
          'Founders can research markets, competitors, and trends.',
          'Marketers can compare claims before turning them into copy.',
        ],
      },
      {
        eyebrow: 'Why GoodGPT is different',
        title: 'It keeps the conversation connected to the reading.',
        body: 'Instead of treating search and chat as separate tasks, GoodGPT keeps the same topic in view. That makes follow-up questions easier and lets you build on prior reading rather than starting over.',
        points: [
          'The topic stays aligned across sessions.',
          'You can ask follow-up questions without resetting the context.',
          'It supports a more natural research rhythm.',
        ],
      },
      {
        eyebrow: 'SEO value',
        title: 'People search for faster research, not just AI tools.',
        body: 'This page targets the intent behind research-related searches: find sources faster, summarize them, and avoid wasting time on fragmented reading. That is exactly what literature discovery is for.',
        points: [
          'Faster research workflow.',
          'Better note-taking outcome.',
          'Clearer route from reading to writing.',
        ],
      },
    ],
    faq: [
      { q: 'Can GoodGPT summarize long sources?', a: 'Yes. Literature discovery is designed to help you extract the important parts of long reading and turn them into something easier to use.' },
      { q: 'Is this only for academic research?', a: 'No. It is useful for product research, market analysis, content planning, and any workflow where reading quickly matters.' },
      { q: 'Why not just use a search engine?', a: 'Search engines find pages. GoodGPT helps you move from pages to understanding, notes, and follow-up questions.' },
    ],
    relatedSlugs: ['persistent-memory', 'presentation-builder'],
    ctaPrimary: { label: 'Start using GoodGPT', href: '/' },
    ctaSecondary: { label: 'Explore the presentation guide', href: '/blog/presentation-builder' },
  },
  {
    slug: 'email-calendar-agent',
    badge: 'Workflow Automation',
    title: 'AI Email and Calendar Agent for Busy People Who Need Less Admin',
    metaTitle: 'AI Email and Calendar Agent for Busy People Who Need Less Admin',
    description: 'GoodGPT email and calendar automation helps you read messages, draft replies, and manage scheduling without switching between apps.',
    excerpt: 'An AI email and calendar agent turns inbox work into a structured workflow so you can reply faster and schedule with less effort.',
    keywords: 'AI email assistant, calendar agent, inbox triage, scheduling AI, GoodGPT email calendar',
    stats: [
      { value: 'Inbox help', label: 'Helps with reading and drafting replies' },
      { value: 'Scheduling', label: 'Supports calendar coordination' },
      { value: 'Less admin', label: 'Reduces repetitive coordination work' },
      { value: 'Focused', label: 'Keeps the workflow in one place' },
    ],
    featured: {
      title: 'What the agent is best at',
      body: 'The right email and calendar assistant should save time on repeat work: sorting, drafting, confirming, and scheduling. GoodGPT focuses on those boring-but-important steps.',
      items: [
        { title: 'Draft faster', text: 'Turn a rough thought into a clean reply without starting from zero.' },
        { title: 'Schedule better', text: 'Keep the conversation and the calendar in the same workflow.' },
        { title: 'Follow through', text: 'Reduce dropped tasks and missed replies.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Inbox triage',
        title: 'Start with the message, then move to the decision.',
        body: 'A cluttered inbox slows everything down. GoodGPT helps you interpret what the message means and what to do next, which makes it easier to prioritize without manually reviewing everything twice.',
        points: [
          'Read the important part quickly.',
          'Draft a reply with the right tone.',
          'Keep the next action clear and low-friction.',
        ],
      },
      {
        eyebrow: 'Calendar coordination',
        title: 'Scheduling becomes easier when context stays attached.',
        body: 'A calendar task is rarely just a time slot. It usually depends on a thread of messages, priorities, and constraints. The agent helps keep that context together.',
        points: [
          'Reduce scheduling back-and-forth.',
          'Keep meeting details connected to the original request.',
          'Handle common coordination tasks faster.',
        ],
      },
      {
        eyebrow: 'Ideal users',
        title: 'This feature is strongest for people who live in their inbox.',
        body: 'Founders, operators, managers, assistants, and client-facing teams spend a lot of time turning messages into decisions. GoodGPT is built to make that work lighter.',
        points: [
          'Operations and support teams.',
          'Founders managing many threads at once.',
          'Professionals who need faster response drafting.',
        ],
      },
      {
        eyebrow: 'Search intent',
        title: 'People want an AI that can reduce inbox load.',
        body: 'This page targets searches around AI email assistant, calendar automation, and inbox triage. It explains the practical outcome: less admin, faster replies, and fewer missed follow-ups.',
        points: [
          'Better inbox productivity.',
          'Lower coordination overhead.',
          'Cleaner move from message to action.',
        ],
      },
    ],
    faq: [
      { q: 'Can the agent help draft replies?', a: 'Yes. Drafting and rewriting responses is one of the main reasons to use an AI email agent.' },
      { q: 'Does it replace my calendar app?', a: 'No. It works with your workflow and helps you manage scheduling more efficiently without replacing the tools you already use.' },
      { q: 'Who gets the most value from this feature?', a: 'People who spend a lot of time coordinating meetings, replying to threads, or managing inbox volume usually get the biggest benefit.' },
    ],
    relatedSlugs: ['smart-chat', 'persistent-memory'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read about persistent memory', href: '/blog/persistent-memory' },
  },
  {
    slug: 'smart-chat',
    badge: 'Core Feature',
    title: 'Smart Chat for Fast Answers, Drafts, and Better Follow-Up Thinking',
    metaTitle: 'Smart Chat for Fast Answers, Drafts, and Better Follow-Up Thinking',
    description: 'Smart Chat in GoodGPT helps you brainstorm, draft, analyze, and continue conversations with better context and less repetition.',
    excerpt: 'Smart Chat is the everyday assistant layer of GoodGPT: fast, flexible, and connected to your broader memory and workflow setup.',
    keywords: 'smart chat AI, AI chat assistant, contextual chat, GoodGPT chat, drafting assistant',
    stats: [
      { value: 'Fast', label: 'Quick responses for everyday work' },
      { value: 'Contextual', label: 'Stays aligned with the current thread' },
      { value: 'Flexible', label: 'Useful for drafting, ideas, and analysis' },
      { value: 'Connected', label: 'Works alongside memory and other tools' },
    ],
    featured: {
      title: 'What makes smart chat useful',
      body: 'A smart chat experience should not feel like a blank box. It should help you think, draft, and continue a conversation without losing the thread.',
      items: [
        { title: 'Brainstorm', text: 'Generate ideas when you need options quickly.' },
        { title: 'Draft', text: 'Turn rough thoughts into a useful first version.' },
        { title: 'Continue', text: 'Pick up where the last conversation left off.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Everyday value',
        title: 'Smart chat is the fastest way to turn intent into output.',
        body: 'Most users do not need a complex workflow every time. Sometimes they just need a quick response, a summary, or a draft. Smart Chat is built for that daily rhythm.',
        points: [
          'Ask a question and move forward immediately.',
          'Draft messages, ideas, and short-form content faster.',
          'Keep the conversation useful instead of generic.',
        ],
      },
      {
        eyebrow: 'With memory',
        title: 'The chat gets better when it knows the background.',
        body: 'Smart Chat becomes more valuable when it can remember the context around your work. That keeps answers from sounding generic and makes follow-up questions easier to ask.',
        points: [
          'Answers can reflect prior goals and preferences.',
          'Follow-up prompts can build on earlier context.',
          'The assistant feels more like a working partner.',
        ],
      },
      {
        eyebrow: 'What people search for',
        title: 'Users want a smart chat AI that saves time.',
        body: 'This page targets the intent behind everyday AI chat searches: quick help, useful drafts, and a conversation that does not feel like starting over each time.',
        points: [
          'Fast chat assistant.',
          'Context-aware response generation.',
          'Simple, useful, everyday AI support.',
        ],
      },
      {
        eyebrow: 'How to use it',
        title: 'Use smart chat as the front door to GoodGPT.',
        body: 'When you need a quick answer, smart chat is the easiest way in. When you need more structure, it can hand off to research, memory, or a task-specific tool.',
        points: [
          'Start with a question or draft.',
          'Refine the result with follow-up prompts.',
          'Move into another GoodGPT feature when the task grows.',
        ],
      },
    ],
    faq: [
      { q: 'What is smart chat best for?', a: 'It is best for quick questions, drafting, brainstorming, and fast follow-up work where context matters.' },
      { q: 'How is it different from generic chat?', a: 'GoodGPT smart chat is connected to memory and other features, so it can stay aligned with your ongoing work.' },
      { q: 'Can it help with writing?', a: 'Yes. It is useful for first drafts, rewrites, and polishing ideas into more usable text.' },
    ],
    relatedSlugs: ['persistent-memory', 'ai-discuss'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read AI Discuss guide', href: '/blog/ai-discuss' },
  },
  {
    slug: 'ai-discuss',
    badge: 'Learning Feature',
    title: 'AI Discuss: A Better Way to Learn, Think, and Practice Ideas',
    metaTitle: 'AI Discuss: A Better Way to Learn, Think, and Practice Ideas',
    description: 'GoodGPT AI Discuss helps users learn, brainstorm, and practice ideas in a conversation that adapts to their level and goals.',
    excerpt: 'AI Discuss is designed for thinking out loud with an assistant that can challenge, explain, and adapt to your level.',
    keywords: 'AI discuss, AI learning assistant, coaching AI, language support, GoodGPT discuss',
    stats: [
      { value: 'Coaching', label: 'Supports learning and reflection' },
      { value: 'Adaptive', label: 'Adjusts to the conversation level' },
      { value: 'Multilingual', label: 'Supports 7 languages in the app' },
      { value: 'Practical', label: 'Useful for practice, prep, and review' },
    ],
    featured: {
      title: 'What AI Discuss is designed to do',
      body: 'AI Discuss is not just another chat mode. It is built to help you think through ideas, rehearse responses, and get better at explaining your own thinking.',
      items: [
        { title: 'Practice', text: 'Use it to rehearse interviews, presentations, or difficult conversations.' },
        { title: 'Learn', text: 'Ask for explanations that match your current knowledge level.' },
        { title: 'Reflect', text: 'Work through ideas until the next step becomes clear.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Learning flow',
        title: 'AI Discuss helps you practice by talking, not just reading.',
        body: 'Some topics become clearer when you explain them out loud. GoodGPT uses AI Discuss to create that kind of feedback loop, where the model can respond, question, and clarify as you go.',
        points: [
          'Learn concepts by conversation.',
          'Practice framing and explanation.',
          'Use guided back-and-forth rather than static output.',
        ],
      },
      {
        eyebrow: 'Communication support',
        title: 'It is useful when you need to say something better.',
        body: 'People often search for AI coaching because they want to express themselves more clearly. AI Discuss helps you work through phrasing, confidence, and structure before you send or present anything.',
        points: [
          'Preparation for interviews.',
          'Support for presentations and calls.',
          'A safer place to rehearse difficult wording.',
        ],
      },
      {
        eyebrow: 'Multilingual value',
        title: 'It is built for multilingual practice and support.',
        body: 'GoodGPT supports AI Discuss in seven languages, which makes it more useful for users who need practice or explanation in more than one language.',
        points: [
          'Better for non-English workflows.',
          'Useful for language learners and multilingual teams.',
          'Makes support feel more accessible.',
        ],
      },
      {
        eyebrow: 'SEO intent',
        title: 'Searchers want a coaching-style AI that helps them think.',
        body: 'This page targets AI coaching, learning assistant, and multilingual conversation intent. The promise is simple: use conversation to sharpen ideas and confidence.',
        points: [
          'AI coaching assistant.',
          'Learning and practice support.',
          'Conversation-driven improvement.',
        ],
      },
    ],
    faq: [
      { q: 'Is AI Discuss the same as chat?', a: 'It overlaps with chat, but it is designed more explicitly for practice, explanation, and coaching-style conversation.' },
      { q: 'Can it help with interview prep?', a: 'Yes. Practicing answers and refining your reasoning is one of the best use cases for AI Discuss.' },
      { q: 'Does it support other languages?', a: 'Yes. GoodGPT currently supports seven languages for AI Discuss and voice-based interactions.' },
    ],
    relatedSlugs: ['career-quest', 'smart-chat'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read Boardroom guide', href: '/blog/career-quest' },
  },
  {
    slug: 'career-quest',
    badge: 'Skill Builder',
    title: 'Boardroom: AI Skill Missions That Build Workplace Confidence',
    metaTitle: 'Boardroom: AI Skill Missions That Build Workplace Confidence',
    description: 'GoodGPT Boardroom uses mission-based learning, feedback, and XP to help users build workplace skills with more consistency.',
    excerpt: 'Boardroom turns professional growth into a guided system of missions, feedback, and progression rather than another generic learning feed.',
    keywords: 'career quest, skill missions, workplace learning, AI coaching, GoodGPT career growth',
    stats: [
      { value: 'Missions', label: 'Turns learning into guided tasks' },
      { value: 'XP', label: 'Rewards consistent progress' },
      { value: 'Feedback', label: 'Makes improvement visible' },
      { value: 'Growth', label: 'Focuses on workplace skills' },
    ],
    featured: {
      title: 'Why mission-based learning works',
      body: 'Career growth sticks better when it is concrete. GoodGPT uses missions and feedback loops to make the next step obvious and the progress visible.',
      items: [
        { title: 'Practice', text: 'Break skill growth into clear scenarios and repeatable actions.' },
        { title: 'Progress', text: 'Use XP and feedback to see momentum over time.' },
        { title: 'Confidence', text: 'Build comfort before the high-stakes moment happens.' },
      ],
    },
    sections: [
      {
        eyebrow: 'What it is',
        title: 'Boardroom is a structured AI career coach.',
        body: 'Instead of leaving users with vague advice, Boardroom gives them missions, prompts, and a progression model that feels more like training and less like passive reading.',
        points: [
          'Practice workplace scenarios directly.',
          'See the next step instead of a broad suggestion.',
          'Track progress with visible feedback.',
        ],
      },
      {
        eyebrow: 'Who it helps',
        title: 'It is built for people improving communication, leadership, and execution.',
        body: 'Boardroom is especially useful when the skill gap is practical rather than theoretical. That includes speaking clearly, giving feedback, handling conflict, and managing time.',
        points: [
          'Professionals preparing for promotion.',
          'Students getting ready for interviews.',
          'Founders and managers building leadership habits.',
        ],
      },
      {
        eyebrow: 'Why it is sticky',
        title: 'You can see progress instead of just reading advice.',
        body: 'The combination of missions and XP makes the experience easier to return to. That matters for SEO too, because people often search for skill-building tools that feel actionable, not abstract.',
        points: [
          'Actionable tasks instead of vague advice.',
          'Visible momentum over time.',
          'A reason to come back and continue.',
        ],
      },
      {
        eyebrow: 'SEO angle',
        title: 'This page matches intent around career coaching and skill development.',
        body: 'People search for career growth, interview practice, and communication training. Boardroom answers that intent with a productized learning loop rather than another course page.',
        points: [
          'AI career coach.',
          'Workplace skill training.',
          'Mission-based learning.',
        ],
      },
    ],
    faq: [
      { q: 'Is Boardroom a course?', a: 'It is more like a guided practice system than a static course. The focus is on doing, feedback, and progression.' },
      { q: 'What skills can it support?', a: 'It is designed for workplace skills such as communication, leadership, interviews, and related growth areas.' },
      { q: 'Why use it instead of a generic coach?', a: 'Boardroom turns growth into concrete missions and progress tracking, which makes it easier to stay engaged.' },
    ],
    relatedSlugs: ['ai-discuss', 'presentation-builder'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read presentation guide', href: '/blog/presentation-builder' },
  },
  {
    slug: 'presentation-builder',
    badge: 'Productivity Feature',
    title: 'AI Presentation Builder: Turn Ideas Into Slides Faster',
    metaTitle: 'AI Presentation Builder: Turn Ideas Into Slides Faster',
    description: 'GoodGPT presentation builder helps turn a topic or prompt into a structured slide deck backed by research and clear messaging.',
    excerpt: 'The presentation builder is for anyone who needs a polished deck quickly without spending the whole day on structure and formatting.',
    keywords: 'AI presentation builder, slide deck generator, presentation software, GoodGPT presentations',
    stats: [
      { value: 'Prompt to deck', label: 'Starts from a simple topic or brief' },
      { value: 'Structured', label: 'Produces a clear narrative flow' },
      { value: 'Research-backed', label: 'Supports evidence and substance' },
      { value: 'Time-saving', label: 'Cuts early-stage deck friction' },
    ],
    featured: {
      title: 'Why presentation generation matters',
      body: 'Most presentations fail before the first slide is polished. They fail at the structure stage. GoodGPT helps solve that by turning a topic into a coherent outline faster.',
      items: [
        { title: 'Outline', text: 'Go from topic to structure without staring at a blank page.' },
        { title: 'Content', text: 'Turn the outline into talking points and supporting material.' },
        { title: 'Delivery', text: 'Get a cleaner starting point for the final deck.' },
      ],
    },
    sections: [
      {
        eyebrow: 'The workflow',
        title: 'Start with a topic, then build the story.',
        body: 'An effective deck is more than a slide collection. GoodGPT helps create the narrative structure first, which makes the final presentation easier to refine.',
        points: [
          'Begin with a topic, goal, or rough outline.',
          'Shape the message into a slide-ready structure.',
          'Improve the deck instead of building it from zero.',
        ],
      },
      {
        eyebrow: 'Practical use',
        title: 'It is useful for pitches, meetings, teaching, and internal updates.',
        body: 'If you present often, the time savings add up quickly. The best use cases are the ones where you need a credible first draft before design polish.',
        points: [
          'Sales and product pitches.',
          'Team meetings and status updates.',
          'Teaching, workshops, and explainers.',
        ],
      },
      {
        eyebrow: 'SEO intent',
        title: 'People search for presentation tools that save time and improve structure.',
        body: 'This page targets the search intent around AI slide decks, presentation builder tools, and faster content planning. The value proposition is simple: better structure in less time.',
        points: [
          'AI slide deck generator.',
          'Presentation outline tool.',
          'Faster first draft of the deck.',
        ],
      },
      {
        eyebrow: 'How GoodGPT helps',
        title: 'The assistant handles the thinking work before the design work.',
        body: 'That means less time staring at an empty slide and more time refining the message, the order of ideas, and the supporting points.',
        points: [
          'Message first, formatting second.',
          'Research-backed starting points.',
          'Cleaner handoff to final presentation design.',
        ],
      },
    ],
    faq: [
      { q: 'Can it help with slide structure?', a: 'Yes. It is useful for turning a rough idea into a more organized presentation outline.' },
      { q: 'Is it only for business decks?', a: 'No. It works for teaching, internal communication, workshops, and other types of presentations too.' },
      { q: 'Why use an AI presentation builder?', a: 'It removes the blank-page problem and speeds up the early stage where most deck work gets stuck.' },
    ],
    relatedSlugs: ['literature-discovery', 'smart-chat'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read literature discovery guide', href: '/blog/literature-discovery' },
  },
  {
    slug: 'chrome-extension',
    badge: 'Chrome Extension',
    title: 'GoodGPT Chrome Extension: Summarize, Paraphrase, and Draft in Browser',
    metaTitle: 'GoodGPT Chrome Extension: Summarize, Paraphrase, and Draft in Browser',
    description: 'The GoodGPT Chrome extension turns selected text into quick AI actions for correction, summarization, paraphrasing, and blog drafting.',
    excerpt: 'The Chrome extension brings GoodGPT into the tab you already have open, so you can act on selected text without leaving the browser.',
    keywords: 'GoodGPT Chrome extension, summarize text, paraphrase text, browser AI assistant, Chrome Web Store',
    stats: [
      { value: 'Selection-aware', label: 'Acts on highlighted text' },
      { value: 'Context menu', label: 'Supports quick actions from Chrome' },
      { value: 'Fast workflow', label: 'Keeps the browser as the working surface' },
      { value: 'Browser-native', label: 'Fits the Chrome workflow naturally' },
    ],
    featured: {
      title: 'Built for quick browser actions',
      body: 'The extension works best when the text is already on the screen and the next step is obvious. That makes it ideal for reading, editing, and drafting workflows.',
      items: [
        { title: 'Read faster', text: 'Summarize long pages or extract only the parts you need.' },
        { title: 'Edit faster', text: 'Fix grammar, tighten tone, and clean up rough writing in place.' },
        { title: 'Draft faster', text: 'Convert notes or selected passages into blog or chat prompts quickly.' },
      ],
    },
    sections: [
      {
        eyebrow: 'What it does',
        title: 'It turns selected text into an AI action.',
        body: 'Highlight text, open the menu, and ask GoodGPT to correct, summarize, paraphrase, write a blog draft, or continue the conversation in chat.',
        points: [
          'Correct grammar and sharpen phrasing.',
          'Summarize long pages or research passages quickly.',
          'Paraphrase or rework text without leaving the browser tab.',
        ],
      },
      {
        eyebrow: 'Why it helps',
        title: 'The value is in removing friction.',
        body: 'When the browser is already open and the text is already there, the ideal AI tool is the one that gets out of the way. The extension keeps the action close to the source text.',
        points: [
          'Less tab switching.',
          'Less copy and paste.',
          'More momentum from reading to action.',
        ],
      },
      {
        eyebrow: 'Best use cases',
        title: 'It is built for reading, editing, and drafting.',
        body: 'If you spend time reading articles, reviewing messages, or cleaning up rough notes, the extension helps you move faster.',
        points: [
          'Turn reading into writing faster.',
          'Edit directly where you found the text.',
          'Use it for quick rewriting and summarization tasks.',
        ],
      },
      {
        eyebrow: 'SEO intent',
        title: 'This page targets browser AI assistant searches.',
        body: 'People search for Chrome extension tools that summarize text, rewrite copy, and speed up research. This page speaks directly to that intent.',
        points: [
          'Chrome AI extension.',
          'Summarize text in browser.',
          'Paraphrase and draft faster.',
        ],
      },
    ],
    faq: [
      { q: 'Does the extension work on most pages?', a: 'It is designed for pages where you can select text and open the context menu, which covers most reading and editing workflows.' },
      { q: 'Can I use it for blogs and notes?', a: 'Yes. The write-blog flow is a good fit when you already have source text and want a fast first draft.' },
      { q: 'Why install it instead of copying text into the app?', a: 'Because it keeps the workflow inside Chrome and removes the friction of moving text between tabs.' },
    ],
    relatedSlugs: ['persistent-memory', 'smart-chat'],
    ctaPrimary: { label: 'Install the extension', href: CHROME_EXTENSION_URL },
    ctaSecondary: { label: 'Read the blog index', href: '/blog' },
  },
  {
    slug: 'memory-workflows',
    badge: 'Workflow Strategy',
    title: 'AI Memory Workflows: How to Stop Repeating Yourself in Every Session',
    metaTitle: 'AI Memory Workflows: How to Stop Repeating Yourself in Every Session',
    description: 'See how GoodGPT memory helps you keep goals, preferences, and project context attached to your work across sessions.',
    excerpt: 'If your assistant keeps forgetting the project, the tone, or the next step, memory workflows turn scattered chats into a continuous working system.',
    keywords: 'AI memory workflows, persistent memory AI, contextual assistant, memory-based productivity, GoodGPT memory',
    stats: [
      { value: 'Context-aware', label: 'Keeps goals and preferences attached to the thread' },
      { value: 'Less repetition', label: 'Reduces re-explaining the same background' },
      { value: 'Faster follow-up', label: 'Makes return visits more productive' },
      { value: 'More consistent', label: 'Helps answers stay aligned over time' },
    ],
    featured: {
      title: 'What a memory workflow actually changes',
      body: 'A good memory setup is not about storing everything. It is about keeping the right context available so you can move from one session to the next without rebuilding the setup from scratch.',
      items: [
        { title: 'Remember', text: 'Keep goals, names, preferences, and current priorities available when they matter.' },
        { title: 'Reuse', text: 'Carry the same context into future prompts and follow-ups.' },
        { title: 'Refine', text: 'Improve the assistant’s output as it learns more about your style.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Why it matters',
        title: 'Without memory, every session starts from zero.',
        body: 'That is fine for quick questions, but it is inefficient for recurring work. Memory workflows let GoodGPT pick up the thread so you spend less time resetting the context and more time getting useful output.',
        points: [
          'Reduce repeated explanations.',
          'Keep important preferences close to the task.',
          'Make follow-up sessions feel continuous.',
        ],
      },
      {
        eyebrow: 'What to store',
        title: 'Store the context that changes how the next answer should be written.',
        body: 'The best memories are the ones that change the quality of the next response: your project topic, tone preference, audience, deadlines, and recurring constraints.',
        points: [
          'Project goals and current status.',
          'Tone, format, and writing style preferences.',
          'Names, teams, and recurring business context.',
        ],
      },
      {
        eyebrow: 'How to use it daily',
        title: 'Treat memory like a working layer, not an archive.',
        body: 'GoodGPT works best when memory is used to support a current task. The assistant should bring relevant context forward when it helps, then stay out of the way when it does not.',
        points: [
          'Use the same assistant for recurring work.',
          'Refine your preferences over time.',
          'Ask follow-up questions instead of starting over.',
        ],
      },
      {
        eyebrow: 'Who benefits most',
        title: 'Memory is strongest for recurring work and longer projects.',
        body: 'People who revisit the same topic, clients, or tasks again and again get the biggest return. That includes researchers, operators, founders, writers, and anyone managing multiple ongoing threads.',
        points: [
          'Repeat work across days or weeks.',
          'Longer projects that need continuity.',
          'Users who want a more personal assistant.',
        ],
      },
    ],
    faq: [
      { q: 'Does memory store everything I say?', a: 'No. The goal is to keep useful context that improves future responses, not to archive every sentence forever.' },
      { q: 'What kind of context is most useful?', a: 'Goals, preferences, project status, audience, tone, and recurring constraints usually matter most.' },
      { q: 'Why does memory improve productivity?', a: 'Because it cuts repetition and keeps the assistant aligned with the work you are actually doing.' },
    ],
    relatedSlugs: ['persistent-memory', 'smart-chat', 'career-quest'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read about smart chat', href: '/blog/smart-chat' },
  },
  {
    slug: 'research-to-writing',
    badge: 'Research Workflow',
    title: 'From Research to Draft: A Better Workflow for Turning Sources Into Writing',
    metaTitle: 'From Research to Draft: A Better Workflow for Turning Sources Into Writing',
    description: 'Learn how GoodGPT helps you move from reading and note-taking to a structured first draft without losing the thread.',
    excerpt: 'The gap between research and writing is where most projects slow down. A better workflow keeps the source material, summary, and draft connected.',
    keywords: 'research to writing workflow, AI writing workflow, literature discovery, source summarization, GoodGPT blog',
    stats: [
      { value: 'Source-aware', label: 'Keeps the original reading context in view' },
      { value: 'Summaries', label: 'Turns long reading into usable notes' },
      { value: 'Draft-ready', label: 'Moves toward an outline faster' },
      { value: 'Less tab chaos', label: 'Reduces switching between tools' },
    ],
    featured: {
      title: 'What changes when research and writing stay connected',
      body: 'Most writing projects break down because the notes live in one place, the sources in another, and the draft in a third. GoodGPT works better when those steps are part of the same flow.',
      items: [
        { title: 'Find', text: 'Use research to narrow the topic and identify the most relevant source material.' },
        { title: 'Organize', text: 'Turn notes into a structure you can actually write from.' },
        { title: 'Draft', text: 'Move from outline to first version without losing the context.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Start with the question',
        title: 'A clear question keeps the research useful.',
        body: 'Before collecting sources, define the outcome. GoodGPT’s literature discovery workflow works best when you know what decision, article, or presentation the research needs to support.',
        points: [
          'Define the exact question or topic.',
          'Decide what the final output should be.',
          'Keep the search narrow enough to be useful.',
        ],
      },
      {
        eyebrow: 'Turn notes into an outline',
        title: 'Summaries become more valuable when they are shaped into sections.',
        body: 'A source summary is useful, but an outline is better. It gives the writing process structure and makes the next step obvious instead of leaving you with a pile of disconnected notes.',
        points: [
          'Group related ideas together.',
          'Identify the key points you want to argue.',
          'Use the outline as the bridge to the draft.',
        ],
      },
      {
        eyebrow: 'Write the first draft',
        title: 'Do not aim for polish first.',
        body: 'The goal of the first draft is momentum. GoodGPT can help you turn the outline into a usable first pass, which is usually more important than perfect wording at this stage.',
        points: [
          'Get the structure on the page quickly.',
          'Fill in the supporting ideas section by section.',
          'Use the assistant to maintain a consistent tone.',
        ],
      },
      {
        eyebrow: 'Review with context',
        title: 'The best edits happen when the source context is still attached.',
        body: 'Once the draft exists, the assistant can help tighten phrasing, compare claims against the source material, and improve clarity without disconnecting the writing from the research.',
        points: [
          'Check that key claims still match the sources.',
          'Polish structure and transitions.',
          'Keep follow-up edits grounded in the original context.',
        ],
      },
    ],
    faq: [
      { q: 'Why is research-to-writing a hard workflow?', a: 'Because the useful context tends to get lost between reading, note-taking, and drafting.' },
      { q: 'How does GoodGPT help?', a: 'It keeps the reading, summary, and draft steps in one workflow so you can move faster.' },
      { q: 'Who should use this workflow?', a: 'Students, marketers, founders, analysts, and writers who work from sources will benefit the most.' },
    ],
    relatedSlugs: ['literature-discovery', 'presentation-builder', 'smart-chat'],
    ctaPrimary: { label: 'Start using GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read literature discovery', href: '/blog/literature-discovery' },
  },
  {
    slug: 'browser-workflows',
    badge: 'Chrome Extension',
    title: 'Chrome Extension AI Workflows: Summarize, Rephrase, and Draft Without Leaving the Tab',
    metaTitle: 'Chrome Extension AI Workflows: Summarize, Rephrase, and Draft Without Leaving the Tab',
    description: 'Use the GoodGPT Chrome extension to turn highlighted text into a faster writing and reading workflow.',
    excerpt: 'The browser extension is best when the next step is obvious: you already have text on screen and need to act on it faster.',
    keywords: 'Chrome extension AI workflow, summarize in browser, rewrite selected text, browser assistant, GoodGPT extension',
    stats: [
      { value: 'Selection-aware', label: 'Works on highlighted text' },
      { value: 'Less copy-paste', label: 'Keeps the work inside the tab' },
      { value: 'Faster edits', label: 'Improves text without switching tools' },
      { value: 'Browser-native', label: 'Fits the workflow you already use' },
    ],
    featured: {
      title: 'Why browser workflows are efficient',
      body: 'The browser is where most reading and editing already happens. A good AI extension removes the need to copy text into another app just to make a quick improvement.',
      items: [
        { title: 'Read', text: 'Summarize long pages or pull the key idea out of dense text.' },
        { title: 'Edit', text: 'Fix grammar, rephrase, or sharpen the tone in place.' },
        { title: 'Draft', text: 'Turn selected content into a better starting point for writing.' },
      ],
    },
    sections: [
      {
        eyebrow: 'When to use it',
        title: 'Use the extension when the content is already on screen.',
        body: 'If you are reading, editing, or reviewing text in the browser, the extension is usually the fastest route because it keeps the action close to the source material.',
        points: [
          'Reading articles or docs.',
          'Cleaning up rough notes.',
          'Rewriting or summarizing selected text.',
        ],
      },
      {
        eyebrow: 'Reading workflow',
        title: 'Summaries shorten the gap between reading and understanding.',
        body: 'The extension helps you extract the important part without losing the thread. That is especially useful when you do not need a long chat, only a fast transformation of the selected content.',
        points: [
          'Extract the important idea quickly.',
          'Reduce tab switching and copy-paste steps.',
          'Keep momentum while you are already in the page.',
        ],
      },
      {
        eyebrow: 'Editing workflow',
        title: 'Great for cleanup jobs and quick rewrites.',
        body: 'The extension is a strong fit for small edits: grammar fixes, paraphrasing, and tone adjustments. Those are the moments where leaving the browser adds unnecessary friction.',
        points: [
          'Correct rough wording fast.',
          'Change the tone without rebuilding the text.',
          'Use the browser as the working surface.',
        ],
      },
      {
        eyebrow: 'Best use cases',
        title: 'It shines when speed matters more than a long chat.',
        body: 'This is the right tool when you want a quick, direct action on the text in front of you. It is less about open-ended brainstorming and more about reducing the work needed to move forward.',
        points: [
          'Reading and summarization.',
          'Editing and paraphrasing.',
          'Drafting from selected source text.',
        ],
      },
    ],
    faq: [
      { q: 'Why use a browser extension instead of copy-pasting?', a: 'Because it keeps the workflow inside the tab and removes extra steps.' },
      { q: 'Does it work for blogs and notes?', a: 'Yes. It is useful whenever you already have source text on screen and want a quick transformation.' },
      { q: 'What makes the browser workflow faster?', a: 'The text is already selected, so the next action happens without moving between tools.' },
    ],
    relatedSlugs: ['chrome-extension', 'smart-chat', 'email-calendar-agent'],
    ctaPrimary: { label: 'Install the extension', href: CHROME_EXTENSION_URL },
    ctaSecondary: { label: 'Read the blog index', href: '/blog' },
  },
  {
    slug: 'email-calendar-automation',
    badge: 'Workflow Automation',
    title: 'Email and Calendar Automation for Busy People Who Need Less Admin',
    metaTitle: 'Email and Calendar Automation for Busy People Who Need Less Admin',
    description: 'See how GoodGPT reduces inbox clutter, speeds up replies, and keeps scheduling context together.',
    excerpt: 'When email and calendar tasks are scattered across tabs, a guided assistant can turn admin into a shorter, cleaner workflow.',
    keywords: 'email calendar automation, AI email assistant, calendar workflow, inbox triage, GoodGPT email agent',
    stats: [
      { value: 'Inbox-aware', label: 'Helps interpret what the message means' },
      { value: 'Scheduling', label: 'Supports coordination and follow-through' },
      { value: 'Faster replies', label: 'Drafts responses with less effort' },
      { value: 'Less admin', label: 'Reduces repetitive coordination work' },
    ],
    featured: {
      title: 'What makes email and calendar work harder than it should be',
      body: 'Most admin work is not one big task. It is a chain of small decisions: read the message, understand the request, draft the response, and schedule the next step. GoodGPT is useful when those steps need to stay connected.',
      items: [
        { title: 'Triage', text: 'Read messages faster and identify what actually needs a response.' },
        { title: 'Draft', text: 'Turn the rough idea into a clear reply with the right tone.' },
        { title: 'Schedule', text: 'Keep the thread and the calendar action together.' },
      ],
    },
    sections: [
      {
        eyebrow: 'Inbox triage',
        title: 'Start by deciding what matters now.',
        body: 'A cluttered inbox is really a prioritization problem. GoodGPT helps you separate urgent requests from background noise so you can focus on the thread that actually needs action.',
        points: [
          'Read the key point quickly.',
          'Spot the next action without re-reading the whole thread.',
          'Move from message to decision faster.',
        ],
      },
      {
        eyebrow: 'Drafting replies',
        title: 'Replies are easier when the context stays attached.',
        body: 'The value of an AI email assistant is not just speed. It is speed with context, so the reply sounds appropriate and the conversation keeps moving in the right direction.',
        points: [
          'Draft responses faster.',
          'Keep the tone aligned with the conversation.',
          'Avoid starting from a blank page.',
        ],
      },
      {
        eyebrow: 'Scheduling',
        title: 'Calendar work should not break the conversation.',
        body: 'Scheduling often requires back-and-forth, so the useful system is the one that keeps the original request attached to the proposed meeting or task.',
        points: [
          'Reduce the number of back-and-forth messages.',
          'Keep meeting context visible.',
          'Make the next step obvious for everyone involved.',
        ],
      },
      {
        eyebrow: 'Who benefits most',
        title: 'The biggest gains go to people living in shared inboxes.',
        body: 'Founders, operators, client-facing teams, assistants, and managers get the most value because they repeatedly turn messages into decisions, replies, and calendar actions.',
        points: [
          'People handling many threads at once.',
          'Teams that schedule meetings all day.',
          'Users who want less repetitive admin.',
        ],
      },
    ],
    faq: [
      { q: 'What is the main benefit of email automation?', a: 'It reduces the time spent reading, drafting, and coordinating the next step.' },
      { q: 'Does GoodGPT replace my inbox or calendar?', a: 'No. It helps you manage them more efficiently without replacing the tools you already use.' },
      { q: 'Who should use this feature?', a: 'Anyone who spends a lot of time turning messages into replies, meetings, or actions.' },
    ],
    relatedSlugs: ['email-calendar-agent', 'persistent-memory', 'smart-chat'],
    ctaPrimary: { label: 'Use GoodGPT', href: '/' },
    ctaSecondary: { label: 'Read about email and calendar agent', href: '/blog/email-calendar-agent' },
  },
];

export const BLOG_POSTS = BLOG_POSTS_DATA.map((post) => ({
  ...post,
  path: postPath(post.slug),
}));

const BLOG_COUNT = BLOG_POSTS.length;
const BLOG_BY_SLUG = Object.fromEntries(BLOG_POSTS.map((post) => [post.slug, post]));

function RelatedPosts({ slugs }) {
  const posts = slugs.map((slug) => BLOG_BY_SLUG[slug]).filter(Boolean);
  if (!posts.length) return null;

  return (
    <section className="blog-related">
      <div className="blog-section-eyebrow">Related</div>
      <h2 className="blog-section-title">More GoodGPT guides</h2>
      <div className="blog-related-grid">
        {posts.map((post) => (
          <Link to={post.path} className="blog-related-card" key={post.slug}>
            <div className="blog-related-badge">{post.badge}</div>
            <div className="blog-related-title">{post.title}</div>
            <div className="blog-related-excerpt">{post.excerpt}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ArticleSection({ eyebrow, title, body, points }) {
  return (
    <section className="blog-section">
      <div className="blog-section-eyebrow">{eyebrow}</div>
      <h2 className="blog-section-title">{title}</h2>
      <p className="blog-section-body">{body}</p>
      <ul className="blog-point-list">
        {points.map((point) => <li key={point}>{point}</li>)}
      </ul>
    </section>
  );
}

function FaqList({ items }) {
  return (
    <section className="blog-faq">
      <div className="blog-section-eyebrow">FAQ</div>
      <h2 className="blog-section-title">Frequently asked questions</h2>
      <div className="blog-faq-list">
        {items.map((item) => (
          <details className="blog-faq-item" key={item.q}>
            <summary className="blog-faq-q">{item.q}</summary>
            <div className="blog-faq-a">{item.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

function BlogLayout({ post }) {
  return (
    <div className={`blog-page ${post.slug === 'chrome-extension' ? 'blog-page--extension' : 'blog-page--feature'}`}>
      <header className="blog-nav">
        <div className="blog-nav-inner">
          <a href="/" className="blog-logo" aria-label="GoodGPT home">
            <span className="blog-logo-icon">🔆</span>
            <span className="blog-logo-text">Good<span>GPT</span></span>
          </a>
          <nav className="blog-links">
            <a href="/blog">Blog index</a>
            <a href="/">GoodGPT app</a>
            <a href={CHROME_EXTENSION_URL} target="_blank" rel="noreferrer">Chrome extension</a>
          </nav>
        </div>
      </header>

      <main className="blog-main">
        <section className="blog-hero">
          <div className="blog-hero-copy">
            <div className="blog-badge">{post.badge}</div>
            <h1 className="blog-title">{post.title}</h1>
            <p className="blog-intro">{post.description}</p>
            <div className="blog-actions">
              <a className="blog-btn blog-btn-primary" href={post.ctaPrimary?.href || DEFAULT_CTA_PRIMARY.href}>{post.ctaPrimary?.label || DEFAULT_CTA_PRIMARY.label}</a>
              <a className="blog-btn blog-btn-secondary" href={post.ctaSecondary?.href || DEFAULT_CTA_SECONDARY.href}>{post.ctaSecondary?.label || DEFAULT_CTA_SECONDARY.label}</a>
            </div>
            <p className="blog-note">{post.excerpt}</p>
          </div>

          <aside className="blog-hero-card">
            <div className="blog-hero-card-label">At a glance</div>
            <div className="blog-stats">
              {post.stats.map((item) => (
                <div className="blog-stat" key={item.value}>
                  <div className="blog-stat-value">{item.value}</div>
                  <div className="blog-stat-label">{item.label}</div>
                </div>
              ))}
            </div>
          </aside>
        </section>

        <section className="blog-content-grid">
          {post.sections.map((section) => <ArticleSection key={section.title} {...section} />)}
        </section>

        <section className="blog-featured">
          <div className="blog-section-eyebrow">Featured</div>
          <h2 className="blog-section-title">{post.featured.title}</h2>
          <p className="blog-section-body">{post.featured.body}</p>
          <div className="blog-featured-grid">
            {post.featured.items.map((item) => (
              <div className="blog-featured-card" key={item.title}>
                <div className="blog-featured-title">{item.title}</div>
                <div className="blog-featured-text">{item.text}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="blog-callout">
          <div className="blog-callout-copy">
            <div className="blog-section-eyebrow">Next step</div>
            <h2 className="blog-section-title">Use the product, then read the next guide.</h2>
            <p className="blog-section-body">
              GoodGPT works best when it becomes part of your routine. Use the app or extension, then continue with the feature guides that match your workflow.
            </p>
          </div>
          <div className="blog-actions blog-callout-actions">
            <a className="blog-btn blog-btn-primary" href={post.ctaPrimary?.href || DEFAULT_CTA_PRIMARY.href}>{post.ctaPrimary?.label || DEFAULT_CTA_PRIMARY.label}</a>
            <a className="blog-btn blog-btn-secondary" href="/blog">{DEFAULT_CTA_SECONDARY.label}</a>
          </div>
        </section>

        <FaqList items={post.faq} />
        <RelatedPosts slugs={post.relatedSlugs} />
      </main>
    </div>
  );
}

function BlogIndexCard({ post }) {
  return (
    <Link to={post.path} className="blog-index-card">
      <div className="blog-index-card-top">
        <span className="blog-index-badge">{post.badge}</span>
        <span className="blog-index-arrow">→</span>
      </div>
      <h2 className="blog-index-card-title">{post.title}</h2>
      <p className="blog-index-card-copy">{post.excerpt}</p>
      <div className="blog-index-card-foot">
        <span>{post.stats[0].value}</span>
        <span>{post.stats[1].value}</span>
      </div>
    </Link>
  );
}

export function BlogIndexPage() {
  return (
    <div className="blog-page blog-page--index">
      <header className="blog-nav">
        <div className="blog-nav-inner">
          <a href="/" className="blog-logo" aria-label="GoodGPT home">
            <span className="blog-logo-icon">🔆</span>
            <span className="blog-logo-text">Good<span>GPT</span></span>
          </a>
          <nav className="blog-links">
            <a href="/">GoodGPT app</a>
            <a href={CHROME_EXTENSION_URL} target="_blank" rel="noreferrer">Chrome extension</a>
          </nav>
        </div>
      </header>

      <main className="blog-main">
        <section className="blog-index-hero">
          <div className="blog-hero-copy">
            <div className="blog-badge">SEO Blog</div>
            <h1 className="blog-title">GoodGPT feature guides, written for search and usefulness.</h1>
            <p className="blog-intro">
              This blog library explains the main GoodGPT features in focused, search-friendly pages. Each article is designed to rank for a specific intent and help users understand the product faster.
            </p>
            <div className="blog-actions">
              <a className="blog-btn blog-btn-primary" href="/">Use GoodGPT</a>
              <a className="blog-btn blog-btn-secondary" href={CHROME_EXTENSION_URL} target="_blank" rel="noreferrer">Open Chrome extension</a>
            </div>
          </div>

          <aside className="blog-hero-card">
            <div className="blog-hero-card-label">What you’ll find</div>
            <div className="blog-stats">
              <div className="blog-stat"><div className="blog-stat-value">{BLOG_COUNT} guides</div><div className="blog-stat-label">One per feature or workflow</div></div>
              <div className="blog-stat"><div className="blog-stat-value">SEO focus</div><div className="blog-stat-label">Built for discoverability and internal linking</div></div>
              <div className="blog-stat"><div className="blog-stat-value">Practical</div><div className="blog-stat-label">Written to explain the real value of each feature</div></div>
            </div>
          </aside>
        </section>

        <section className="blog-index-grid">
          {BLOG_POSTS.map((post) => <BlogIndexCard key={post.slug} post={post} />)}
        </section>
      </main>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_BY_SLUG[slug];
  if (!post) return <Navigate to="/blog" replace />;
  return <BlogLayout post={post} />;
}

export function ChromeExtensionBlogPage() {
  return <BlogPostPage />;
}

export function BlogPage() {
  return <BlogIndexPage />;
}

export default BlogPage;
